import React from 'react';

/**
 * Publish the full-bleed band width for this shell: the viewport minus the
 * outlet's classic scrollbar. Bands break out with `100vw`-style geometry, and
 * a scrollbar inside the outlet would otherwise shift every band rail half a
 * scrollbar left of the top bar's rail.
 */
export function useShellBleedWidth(
  rootRef: React.RefObject<HTMLDivElement | null>,
  outletRef: React.RefObject<HTMLDivElement | null>,
): void {
  React.useEffect(() => {
    const root = rootRef.current;
    const outlet = outletRef.current;
    if (!root || !outlet) return undefined;
    let published = -1;
    const measure = (): void => {
      const scrollbar = Math.max(0, outlet.offsetWidth - outlet.clientWidth);
      if (scrollbar === published) return;
      published = scrollbar;
      root.style.setProperty('--fm-bleed-w', `calc(100vw - ${scrollbar}px)`);
    };
    measure();
    // The scrollbar appears and disappears with content height, so watch the
    // routed content as well as the outlet itself.
    const resizeObserver =
      typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    resizeObserver?.observe(outlet);
    const mutationObserver = new MutationObserver(() => {
      resizeObserver?.disconnect();
      resizeObserver?.observe(outlet);
      Array.from(outlet.children).forEach((child) => resizeObserver?.observe(child));
      measure();
    });
    mutationObserver.observe(outlet, { childList: true });
    Array.from(outlet.children).forEach((child) => resizeObserver?.observe(child));
    return () => {
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      root.style.removeProperty('--fm-bleed-w');
    };
  }, [rootRef, outletRef]);
}
